import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import MemberShell from "@/components/MemberShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { authHeaders } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /member/brand-dna — self-serve Brand DNA editor.
 *
 * Members tweak how Kaily generates content for them WITHOUT needing to
 * WhatsApp her. Saves to `members.brand_dna` (dotted-path $set so we don't
 * overwrite other member fields).
 */
export default function MemberBrandDNA() {
    const { lang } = useT();
    const isCn = lang === "cn";
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [email, setEmail] = useState("");
    const [dna, setDna] = useState({
        cafe_name: "",
        instagram_handle: "",
        city: "",
        cuisine: "",
        brand_colour: "",
        voice: "",
        style_sentence: "",
        target_customer: "",
        posting_freq: "",
        notes: "",
    });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [me, bd] = await Promise.all([
                    axios.get(`${API}/auth/me`, { headers: authHeaders() }),
                    axios.get(`${API}/member/hub/brand-dna`, { headers: authHeaders() }),
                ]);
                if (cancelled) return;
                setEmail(me?.data?.member?.email || "");
                setDna((prev) => ({ ...prev, ...(bd?.data || {}) }));
            } catch (err) {
                if (err?.response?.status === 401) {
                    nav("/member/login?next=/member/brand-dna", { replace: true });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [nav]);

    const setField = (key) => (e) => setDna((p) => ({ ...p, [key]: e?.target?.value ?? e }));

    const save = async () => {
        setSaving(true);
        try {
            // Strip empty strings — Pydantic exclude_none in backend.
            const payload = {};
            Object.entries(dna).forEach(([k, v]) => {
                if (v && String(v).trim()) payload[k] = String(v).trim();
            });
            await axios.put(`${API}/member/hub/brand-dna`, payload, { headers: authHeaders() });
            toast.success(isCn ? "Brand DNA 已保存 · Kaily 下次生成会用新设置" : "Brand DNA saved · Kaily will use it on next monthly drop");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cafe-cream">
                <Loader2 className="h-8 w-8 animate-spin text-cafe-olive" />
            </div>
        );
    }

    return (
        <MemberShell email={email}>
            <Link to="/member" className="inline-flex items-center text-cafe-olive hover:text-cafe-oliveDark text-sm mb-6" data-testid="dna-back">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> {isCn ? "返回 Hub" : "Back to Hub"}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl text-cafe-espresso mb-2 leading-tight">
                {isCn ? "Brand DNA" : "Brand DNA"}
            </h1>
            <p className="text-cafe-mocha text-sm mb-8 max-w-2xl">
                {isCn
                    ? "告诉我你的品牌灵魂 — Kaily 会用这些信息为你下个月生成的所有内容定调。"
                    : "Tell me the soul of your brand. Every caption, image and reel I make next month gets tuned by these answers."}
            </p>

            <div className="space-y-5 max-w-2xl" data-testid="dna-form">
                <Section title={isCn ? "基本信息" : "Basics"}>
                    <Grid2>
                        <Field label={isCn ? "咖啡馆名称" : "Cafe name"} value={dna.cafe_name} onChange={setField("cafe_name")} testid="dna-cafe-name" />
                        <Field label="Instagram" value={dna.instagram_handle} onChange={setField("instagram_handle")} placeholder="@yourhandle" testid="dna-ig" />
                    </Grid2>
                    <Grid2>
                        <Field label={isCn ? "城市" : "City"} value={dna.city} onChange={setField("city")} placeholder="Petaling Jaya" testid="dna-city" />
                        <Field label={isCn ? "类型" : "Cuisine / vibe"} value={dna.cuisine} onChange={setField("cuisine")} placeholder="Specialty coffee · matcha · brunch" testid="dna-cuisine" />
                    </Grid2>
                </Section>

                <Section title={isCn ? "视觉" : "Visual identity"}>
                    <Grid2>
                        <Field
                            label={isCn ? "品牌主色（hex）" : "Brand colour (hex)"}
                            value={dna.brand_colour}
                            onChange={setField("brand_colour")}
                            placeholder="#5B4F3A"
                            testid="dna-colour"
                        />
                        <ColorSwatch hex={dna.brand_colour} />
                    </Grid2>
                </Section>

                <Section title={isCn ? "调性" : "Voice & style"}>
                    <TArea
                        label={isCn ? "一句话品牌风格" : "Style in one sentence"}
                        value={dna.style_sentence}
                        onChange={setField("style_sentence")}
                        placeholder="Warm, slow, hand-poured — like a Sunday afternoon"
                        testid="dna-style"
                    />
                    <TArea
                        label={isCn ? "语气" : "Voice (formal / playful / poetic …)"}
                        value={dna.voice}
                        onChange={setField("voice")}
                        placeholder="Conversational + warm, like writing to a friend"
                        rows={2}
                        testid="dna-voice"
                    />
                </Section>

                <Section title={isCn ? "客户与节奏" : "Audience & cadence"}>
                    <TArea
                        label={isCn ? "理想客户" : "Ideal customer"}
                        value={dna.target_customer}
                        onChange={setField("target_customer")}
                        placeholder="Designers in their 20s-30s. Sundays. Bring a book."
                        rows={2}
                        testid="dna-target"
                    />
                    <Field
                        label={isCn ? "发帖频率" : "Posting frequency"}
                        value={dna.posting_freq}
                        onChange={setField("posting_freq")}
                        placeholder="3 feed posts/week + daily stories"
                        testid="dna-freq"
                    />
                </Section>

                <Section title={isCn ? "其他备注" : "Anything else"}>
                    <TArea
                        label={isCn ? "Kaily 应该知道的事情" : "Things Kaily should know"}
                        value={dna.notes}
                        onChange={setField("notes")}
                        placeholder="Avoid neon. Always lowercase. We never show staff faces."
                        rows={4}
                        testid="dna-notes"
                    />
                </Section>

                <div className="sticky bottom-4 z-10 bg-cafe-cream/95 backdrop-blur border border-cafe-espresso/15 rounded-2xl px-5 py-3 flex items-center justify-between shadow-lg">
                    <span className="text-xs text-cafe-mocha">
                        {isCn ? "保存后生效于下个月 digest" : "Saved settings apply to next monthly drop"}
                    </span>
                    <Button
                        onClick={save}
                        disabled={saving}
                        data-testid="dna-save"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-5 text-xs"
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                        {isCn ? "保存全部" : "Save all"}
                    </Button>
                </div>
            </div>
        </MemberShell>
    );
}


function Section({ title, children }) {
    return (
        <div className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-5 md:p-6">
            <h3 className="font-display text-lg text-cafe-espresso mb-4">{title}</h3>
            <div className="space-y-4">{children}</div>
        </div>
    );
}
function Grid2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-cafe-mocha font-semibold">{label}</Label>
            <Input value={value || ""} onChange={onChange} placeholder={placeholder} data-testid={testid}
                className="mt-1 bg-white border-cafe-espresso/10 rounded-lg text-sm h-9" />
        </div>
    );
}
function TArea({ label, value, onChange, placeholder, rows = 3, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-cafe-mocha font-semibold">{label}</Label>
            <Textarea value={value || ""} onChange={onChange} placeholder={placeholder} rows={rows} data-testid={testid}
                className="mt-1 bg-white border-cafe-espresso/10 rounded-lg text-sm" />
        </div>
    );
}
function ColorSwatch({ hex }) {
    const valid = /^#?[0-9a-fA-F]{3,8}$/.test(hex || "");
    const fill = valid ? (hex.startsWith("#") ? hex : `#${hex}`) : "transparent";
    return (
        <div className="flex items-end gap-3">
            <div
                className="h-9 w-12 rounded-lg border border-cafe-espresso/15 mt-5"
                style={{ background: fill }}
                aria-label="Brand colour preview"
            />
            <span className="text-[11px] text-cafe-mocha/70 mb-1">{valid ? "preview" : "enter a valid hex"}</span>
        </div>
    );
}
